import { type FC, useCallback } from "react";

import { type ApolloError } from "@apollo/client";
import { Button } from "@snapchat/snap-design-system";

import { useModal } from "../../../camkit/core/hooks";
import { type ChangedField } from "../../state/lensImmerState";

import { DiscardChangesModal } from "./DiscardChangesModal";
import { animationContainer, discardButtonStyle, saveButtonStyle } from "./SaveChangesComponent.module.css";
import { SaveChangesModal } from "./SaveChangesModal";

interface SaveChangesComponentProps {
    hasChanges: boolean;
    onCancel: () => void;
    onSave: () => Promise<void>;
    loading: boolean;
    isSaveDisabled?: boolean;
    changedFields: ChangedField[];
    updateError: ApolloError | undefined;
}

export const SaveChangesComponent: FC<SaveChangesComponentProps> = ({
    hasChanges,
    onCancel,
    onSave,
    loading,
    isSaveDisabled = false,
    changedFields,
    updateError,
}) => {
    const [saveModalProps, showSaveModal] = useModal();
    const [discardModalProps, showDiscardModal] = useModal();

    const handleConfirmSave = useCallback(async () => {
        await onSave();
        saveModalProps.onCancel();
    }, [onSave, saveModalProps]);

    const handleConfirmDiscard = useCallback(() => {
        onCancel();
        discardModalProps.onCancel();
    }, [onCancel, discardModalProps]);

    return (
        <>
            {hasChanges && (
                <div className={animationContainer}>
                    <Button
                        onClick={showSaveModal}
                        isDisabled={loading || isSaveDisabled}
                        type="primary"
                        data-testid="save-changes-button"
                        className={saveButtonStyle}
                    >
                        Save Changes
                    </Button>
                    <Button
                        onClick={showDiscardModal}
                        isDisabled={loading}
                        data-testid="cancel-changes-button"
                        className={discardButtonStyle}
                    >
                        Discard Changes
                    </Button>
                </div>
            )}

            <SaveChangesModal
                opened={saveModalProps.visible}
                onClose={saveModalProps.onCancel}
                onSave={handleConfirmSave}
                changedFields={changedFields}
                loading={loading}
                updateError={updateError}
            />

            <DiscardChangesModal
                opened={discardModalProps.visible}
                onClose={discardModalProps.onCancel}
                onDiscard={handleConfirmDiscard}
            />
        </>
    );
};
